import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { StatusBadge } from '@/components/common/StatusBadge';
import { dashboardAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { isAgencyPrincipal, getUserAgencyId } from '@/lib/utils';
import { useAnnouncements } from '@/hooks/dashboard/useAnnouncements';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ActivityFeed } from '@/components/common/ActivityFeed';
import { PerformanceScoreboard } from '@/components/common/PerformanceScoreboard';
import { AgencyAuditLog } from '@/components/common/AgencyAuditLog';
import { FollowupQueueWidget } from '@/components/crm/FollowupQueueWidget';
import { DashboardCharts } from '@/components/common/DashboardCharts';
import { PushNotificationToggle } from '@/components/common/PushNotificationToggle';
import { TopBirthdayBanner } from '@/components/dashboard/TopBirthdayBanner';
import {
  Building2, Users, FileText, Ticket, ClipboardList,
  Plus, ArrowRight, Clock, Megaphone,
} from 'lucide-react';
import { DashboardShell } from './DashboardShell';
import { MetricCard } from './MetricCard';
import { AgencyAttentionRequired } from './AgencyAttentionRequired';
import { AgencyContractingHealth } from './AgencyContractingHealth';
import { AgencySnapshot } from './AgencySnapshot';
import RecentPayoutsWidget from './RecentPayoutsWidget';
import UpcomingFollowupsWidget from './UpcomingFollowupsWidget';

/**
 * AgencyDashboard — serves Agency Principals and Agency Users.
 * Principal-only sections (top KPI row + New Request CTA + Quick Actions +
 * ActivityFeed) are gated internally by `isPrincipal`. Everything else is
 * visible to any agency-context user.
 */
export const AgencyDashboard = () => {
  const { user } = useAuth();
  const isPrincipal = isAgencyPrincipal(user);
  const agencyId = getUserAgencyId(user);
  const [stats, setStats] = useState({});
  const [recentRequests, setRecentRequests] = useState([]);
  const { announcements } = useAnnouncements();
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [statsRes, recentRes] = await Promise.all([
        dashboardAPI.stats(),
        dashboardAPI.recentRequests(5),
      ]);
      setStats(statsRes.data);
      setRecentRequests(recentRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardShell
      title="Agency Dashboard"
      description="Monitor your agency activity, contracting pipeline, and team operations."
      actions={
        <div className="flex items-center gap-3">
          <PushNotificationToggle />
          {isPrincipal && (
            <Link to="/request-contract">
              <Button data-testid="new-request-btn"><Plus size={16} className="mr-2" /> New Contract Request</Button>
            </Link>
          )}
        </div>
      }
    >
      <AgencyAttentionRequired
        requestsByStatus={stats?.requests_by_status || {}}
        openTicketsCount={stats?.open_tickets || 0}
        parentLoading={loading}
      />

      <AgencyContractingHealth />

      <AgencySnapshot agencyId={agencyId} />

      <TopBirthdayBanner days={14} />

      {/* Phase 27.21 — Recent Payouts (agency-level earnings tile) */}
      {agencyId && (
        <RecentPayoutsWidget
          recipientType="agency"
          recipientId={agencyId}
          profileHref={`/agencies/${agencyId}?tab=payment`}
          limit={5}
        />
      )}

      {/* Phase 27.28 — Upcoming follow-ups across the agency downline */}
      <UpcomingFollowupsWidget limit={5} />

      {/* Agency Principal Stats */}
      {isPrincipal && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard icon={Building2} value={stats.total_agencies} label="Agencies in Downline" color="bg-slate-900" href="/agencies" loading={loading} />
          <MetricCard icon={Users} value={stats.total_agents} label="Total Agents" color="bg-sky-600" href="/agents" loading={loading} />
          <MetricCard icon={ClipboardList} value={stats.total_requests} label="Contracts" color="bg-emerald-500" href="/requests" loading={loading} />
          <MetricCard icon={Ticket} value={stats.open_tickets} label="Open Tickets" color="bg-amber-500" href="/tickets" loading={loading} />
        </div>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <Card className="mb-8 animate-slideIn border-l-4 border-l-blue-500" data-testid="admin-announcements">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Megaphone size={16} className="text-blue-500" />
              Announcements
            </CardTitle>
            <Link to="/announcements"><Button variant="ghost" size="sm" className="text-xs">Manage <ArrowRight size={12} className="ml-1" /></Button></Link>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {announcements.map((ann) => (
              <div key={ann.announcement_id} className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-slate-900">{ann.title}</p>
                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{ann.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{formatDate(ann.publish_date)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Analytics Charts (Principal only — matches pre-refactor gating) */}
      {isPrincipal && <DashboardCharts />}

      {/* Recent Requests */}
      {recentRequests.length > 0 && (
        <Card className="animate-slideIn stagger-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Clock size={20} className="text-slate-400" />
              Recent Contracts
            </CardTitle>
            <Link to="/requests"><Button variant="outline" size="sm">View All</Button></Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentRequests.map((request, index) => (
                <Link key={request.request_id} to={`/requests/${request.request_id}`}
                  className="block p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                  data-testid={`recent-request-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{request.agent_name}</p>
                      <p className="text-sm text-slate-500">{request.carrier_name} | {request.level_name}</p>
                    </div>
                    <div className="text-right">
                      <StatusBadge status={request.current_status} />
                      <p className="text-xs text-slate-400 mt-1">{formatDate(request.created_at)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions for Principal */}
      {isPrincipal && (
        <Card className="mt-8 animate-slideIn stagger-3">
          <CardHeader><CardTitle className="text-lg font-heading">Quick Actions</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/agents?action=add">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Users size={20} className="mr-3 text-sky-600" />
                  <div className="text-left"><p className="font-medium">Add New Agent</p><p className="text-xs text-slate-500">Register a new agent</p></div>
                </Button>
              </Link>
              <Link to="/request-contract">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <FileText size={20} className="mr-3 text-emerald-600" />
                  <div className="text-left"><p className="font-medium">Request Contract</p><p className="text-xs text-slate-500">Submit a new request</p></div>
                </Button>
              </Link>
              <Link to="/tickets?action=create">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Ticket size={20} className="mr-3 text-amber-600" />
                  <div className="text-left"><p className="font-medium">Create Ticket</p><p className="text-xs text-slate-500">Get support from KR</p></div>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 23.1 — CRM Follow-up Queue */}
      <div className="mt-8">
        <FollowupQueueWidget />
      </div>

      {/* Performance Scoreboard + Agency Audit Log for Principals */}
      {isPrincipal && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <PerformanceScoreboard />
          <AgencyAuditLog />
        </div>
      )}
    </DashboardShell>
  );
};

export default AgencyDashboard;
